import BadRequestError from 'App/Errors/BadRequestError';
import SharpstarCallFailedError from 'App/Errors/SharpstarCallFailedError';
import parseJsonResponse from 'App/Helpers/parseJsonResponse';
import SharpStarDataService from 'App/Services/Sharpstar/SharpStarDataService';

export default class SharpstarServiceHandler {
    /**
     * initialize sharpstar service handler by page setup
     * @param sharpstarBaseUrl
     * @param source
     * @param campaignId
     * @param cookieService
     * @param abortController
     */
    constructor(
        sharpstarBaseUrl,
        source,
        campaignId,
        cookieService,
        abortController
    ) {
        this.SharpStarDataService = new SharpStarDataService(
            sharpstarBaseUrl,
            source,
            campaignId,
            abortController
        );
        this.cookieService = cookieService;
    }

    /**
     * call sharpstar getOffers, sort offers and return as Promise
     * @param hash
     * @param isLead
     * @param rid
     * @param refid1
     * @param refid2
     * @param refid3
     * @param isBot
     * @param visitorId
     * @param customIsBot
     * @param botFailedChecks
     * @returns {Promise<any>}
     */
    async getOffers({
        hash,
        isLead,
        rid,
        refid1,
        refid2,
        refid3,
        isBot,
        customIsBot,
        visitorId,
        botFailedChecks,
    }) {
        let response;

        try {
            response = await this.SharpStarDataService.getOffers({
                hash,
                isLead,
                rid,
                refid1,
                refid2,
                refid3,
                isBot,
                customIsBot,
                visitorId,
                botFailedChecks,
            });
        } catch (error) {
            // Abort (user navigation / cancel) — not a network outage signal
            if (error?.name === 'AbortError') {
                throw error;
            }

            const msg = String(error?.message || '');

            const looksLikeNetwork =
                error instanceof TypeError &&
                (msg.includes('Failed to fetch') ||
                    msg.includes('NetworkError') ||
                    msg.includes('Load failed') ||
                    msg.includes('fetch')); // keep as fallback

            if (looksLikeNetwork) {
                throw new SharpstarCallFailedError(
                    'Network error fetching Get Offers (possible connectivity/CORS/DNS/Adblock).'
                );
            }

            throw error;
        }

        if (!response) {
            throw new SharpstarCallFailedError(
                `No fetch response for getOffers, response value - ${response}`
            );
        }

        const resBody = await parseJsonResponse(response, 'getOffers');

        if (!response.ok) {
            if (resBody.status === 'error') {
                throw new SharpstarCallFailedError(
                    resBody.message,
                    resBody.error_code
                );
            } else if (resBody === 'Bad Request') {
                throw new BadRequestError();
            } else {
                throw new SharpstarCallFailedError(
                    'get offers failed with response not ok'
                );
            }
        }

        if (resBody.status === 'success') {
            this.sortOffers(resBody);
            return resBody;
        } else {
            throw new Error(resBody);
        }
    }

    /**
     * call sharpstar add device info when OneSignal push lead was created and return response body.
     @param browser - string (required)
     @param device - string (required)
     @param isBot - boolean (optional)
     @param uuid - string (required) - refid3 or refid2_refid3 url parameters
     @param extraData - json (optional)
     @param campaignId - number (optional)
     @param deviceId - string (optional)
     @param hash - string (optional) - push lead hash
     @param refid1 - string (optional)
     */
    async addOneSignalLeadCountTest({
        browser,
        device,
        isBot,
        uuid,
        extraData,
        campaignId,
        deviceId,
        hash,
        refid1,
    }) {
        const response =
            await this.SharpStarDataService.addOneSignalLeadCountTest({
                browser,
                device,
                isBot,
                uuid,
                extraData,
                campaignId,
                deviceId,
                hash,
                refid1,
            });

        if (!response.ok) {
            throw new SharpstarCallFailedError(
                'add OneSignal lead count test failed with response not ok'
            );
        } else {
            return await response.json();
        }
    }

    /**
     * call sharpstar addExtraData function to post additional app data
     * @param hash - string (required)
     * @param campaignId - number (required)
     * @param extraData - object (required) - any number of key:value pairs
     */
    async addExtraData({ hash, campaignId, extraData }) {
        const response = await this.SharpStarDataService.addExtraData({
            hash,
            campaignId,
            extraData,
        });
        const resBody = await response.json();

        if (!response.ok) {
            if (resBody.status === 'error') {
                throw new SharpstarCallFailedError(
                    resBody.message,
                    resBody.error_code
                );
            } else if (resBody === 'Bad Request') {
                throw new BadRequestError();
            } else {
                throw new SharpstarCallFailedError(
                    'add extra data failed with response not ok'
                );
            }
        } else {
            return resBody;
        }
    }

    /**
     * call sharpstar creat lead, sort offers, set cookie and return as Promise.
     * @param email
     * @param name
     * @param offerId
     * @param extraData
     * @param cpttknV3
     * @param hp_token
     * @param cpttknV2
     * @param hp_choice
     * @param hp_check
     * @returns {Promise<*>}
     */
    async createLead({
        email,
        name,
        offerId,
        extraData,
        cpttknV3,
        cpttknV2,
        hp_token,
        hp_choice,
        hp_check,
    }) {
        const creatLead = await this.SharpStarDataService.createLead({
            email,
            name,
            offerId,
            extraData,
            cpttknV3,
            cpttknV2,
            hp_token,
            hp_choice,
            hp_check,
        });

        return this.handleRequest(creatLead, 'create lead');
    }

    /**
     * call sharpstar creat push lead, sort offers, set cookie and return as Promise.
     * @param device_id
     * @param offerId
     * @param rid
     * @param refid1
     * @param refid2
     * @param refid3
     * @param extraData
     * @param setStorage
     * @returns {Promise<*>}
     */
    async createPushLead({
        device_id,
        offerId,
        rid,
        refid1,
        refid2,
        refid3,
        extraData,
        setStorage = true,
    }) {
        const createPushLead = await this.SharpStarDataService.createPushLead({
            device_id,
            offerId,
            rid,
            refid1,
            refid2,
            refid3,
            extraData,
        });

        return this.handleRequest(
            createPushLead,
            'create push lead',
            setStorage
        );
    }

    /**
     * call sharpstar add push device, sort offers, set cookie and return as Promise.
     * @param device_id
     * @param hash
     * @param offerId
     * @returns {Promise<*>}
     */
    async addPushDevice({ device_id, hash, offerId }) {
        const addPushDevice = await this.SharpStarDataService.addPushDevice({
            device_id,
            hash,
            offerId,
        });

        return this.handleRequest(addPushDevice, 'add push device');
    }

    /**
     * call sharpstar add contact info, sort offers, set cookie and return as Promise.
     * @param name
     * @param email
     * @param hash
     * @param offerId
     * @param extraData
     * @returns {Promise<*>}
     */
    async addContactInfo({ name, email, hash, offerId, extraData }) {
        const addContactInfo = await this.SharpStarDataService.addContactInfo({
            name,
            email,
            hash,
            offerId,
            extraData,
        });
        return this.handleRequest(addContactInfo, 'add contact info');
    }

    /**
     * call sharpstar playerID,set cookie and return as Promise.
     * @param playerID
     * @returns {Promise<*>}
     */
    async createPlayer(playerID) {
        const createPlayer =
            await this.SharpStarDataService.createPlayer(playerID);

        return this.handleRequest(createPlayer, 'create player');
    }

    /**
     * create player and sync with push lead
     * @param campaign_id
     * @param hash
     * @param playfub_id
     * @returns {Promise<Response>}
     */
    async createPlayerAndSyncLead({ playfab_id, hash, campaign_id }) {
        const createPlayerAndSyncLead =
            await this.SharpStarDataService.createPlayerAndSyncLead({
                hash,
                campaign_id,
                playfab_id,
            });

        return this.handleRequest(
            createPlayerAndSyncLead,
            'create player and sync lead'
        );
    }

    /**
     * sort the response from sharpstar by rating, set cookie and add breadcrumb if fails, return the sorted response.
     * @param req
     * @param {string} requestType
     * @param {boolean} setStorage
     *
     * @returns {Promise<any>}
     */
    async handleRequest(req, requestType, setStorage = true) {
        const res = await req.json();

        if (res.status === 'success') {
            this.sortOffers(res);
            if (setStorage) {
                localStorage.setItem('hash', res.hash);
                this.cookieService.setCookie(
                    res.hash,
                    res.lead.esp_status,
                    res.lead.push_status,
                    this.SharpStarDataService.campaignId
                );
            }
        } else if (res.status === 'error') {
            const errorMessage = requestType ? `${requestType} failed` : '';
            const additionalMessage =
                res.message === 'API error code' ? '' : `, ${res.message}`;

            throw new SharpstarCallFailedError(
                `${errorMessage}${additionalMessage}`,
                res.error_code
            );
        } else if (res === 'Bad Request') {
            throw new BadRequestError();
        }

        return res;
    }

    /**
     * sort response from sharpstar by rating. if there is no rating return false(undefined > undefined => false).
     * @param rule
     * @returns {*}
     */
    sortOffers(rule) {
        return rule.offers.sort((a, b) => (a.rating > b.rating ? 1 : -1));
    }
}
